/**
 * WebSocket configuration constants
 * Centralized configuration for Ably Native Pub/Sub
 *
 * Note: Ably handles reconnection internally, so most reconnection
 * settings are no longer needed. Ably SDK automatically:
 * - Reconnects every 15s (up to 2 minutes), then every 30s
 * - Recovers missed messages on reconnect (2-minute window)
 * - Queues messages during disconnection
 */

export const WEBSOCKET_CONFIG = {
  // Message handling
  MESSAGES: {
    MAX_HISTORY_SIZE: 100,
    RING_BUFFER_SIZE: 200, // Double the history size for circular buffer
  },

  // Batch invalidation settings
  BATCH_INVALIDATION: {
    DELAY_MS: 100,
    MAX_SIZE: 20,
    DEBOUNCE_MS: 50, // Debounce for rapid events
  },

  // Animation rotation (for UI components)
  UI: {
    ANIMATION_ROTATION_INTERVAL: 30000, // 30 seconds
  },

  // Realtime sync settings (for data_changed event)
  REALTIME_SYNC: {
    PROGRESS_THROTTLE_MS: 100, // Throttle progress events
    INVALIDATION_DEBOUNCE_MS: 150, // Group invalidations
    MUTATION_LOCK_MS: 1000, // Lock invalidation during mutations
  },
} as const;

/**
 * Ably connection state constants
 * @see https://ably.com/docs/connect/states
 *
 * States:
 * - initialized: SDK created but not yet connected
 * - connecting: Attempting to establish connection
 * - connected: Active connection with full functionality
 * - disconnected: Temporary failure, auto-reconnecting every 15s (up to 2 min)
 * - suspended: Long-term failure after 2 min, reconnecting every 30s
 * - closing: Connection is closing
 * - closed: Connection closed (explicit disconnect)
 * - failed: Terminal state, requires explicit connect() call
 */
export const ABLY_CONNECTION_STATES = {
  INITIALIZED: 'initialized',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  SUSPENDED: 'suspended',
  CLOSING: 'closing',
  CLOSED: 'closed',
  FAILED: 'failed',
} as const;
